/**
 * 認證狀態管理 Context
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from '../api/axios';
import { UserProfile } from '../types';
import { authService } from '../api/authService';
import { loadLifeData, clearLifeData } from '../services/lifeDataService';

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  chatModeAvailable: boolean;   // E4 D16+D17：聊天模式可用（GET /api/system/my-modes·隨使用者載入）
  chatUnavailableReason: string | null;   // E5 §6-1：not_subscribed / blocked / null
  login: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [chatModeAvailable, setChatModeAvailable] = useState(false);
  const [chatUnavailableReason, setChatUnavailableReason] = useState<string | null>(null);

  // 載入使用者資料
  const loadUser = async () => {
    try {
      if (authService.isAuthenticated()) {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
        // 登入成功後拉取個人生活資料（非同步，不阻塞 UI）
        loadLifeData().catch(e => console.warn('[LifeData] load failed:', e));
        // E4：可用介面模式（聊天模式由訂閱 + 未停用決定）·await 以確保 chatModeAvailable
        // 於 isAuthLoading=false 前settle（AIChatLayout guard 不會因競態誤導頁）
        try {
          const r = await axios.get<{ modes: string[]; chat_unavailable_reason?: string | null }>(
            '/api/system/my-modes');
          setChatModeAvailable((r.data.modes || []).includes('chat'));
          setChatUnavailableReason(r.data.chat_unavailable_reason ?? null);
        } catch {
          setChatModeAvailable(false);   // silent ok: 失敗退回只功能模式
          setChatUnavailableReason('not_subscribed');
        }
      }
    } catch (error) {
      // 未 login user 進 login page（localStorage 殘留過期 token）是 normal case：
      // 401/403 silent（只清 token，不 console.error、不 fire error toast 干擾）；
      // 其他錯誤（500 等）才 log。
      const status = (error as any)?.response?.status;
      if (status !== 401 && status !== 403) {
        console.error('載入使用者資料失敗:', error);
      }
      localStorage.removeItem('access_token');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsAuthLoading(false);
    }
  };

  // 登入
  const login = async (token: string) => {
    authService.saveToken(token);
    await loadUser();
  };

  // 登出
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('登出失敗:', error);
    } finally {
      authService.clearToken();
      clearLifeData();   // 清除個人生活資料快取
      setUser(null);
      setIsAuthenticated(false);
      setChatModeAvailable(false);
      setChatUnavailableReason(null);
    }
  };

  // 重新載入使用者資料
  const refreshUser = async () => {
    await loadUser();
  };

  // 初始化載入使用者
  useEffect(() => {
    loadUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isAuthLoading,
        chatModeAvailable,
        chatUnavailableReason,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// 自訂 Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
